import { EventDispatcher } from 'three';
import ThreeShell from './three/ThreeShell';
import Ticker from '../../vendors/ngab/animation/Ticker';
import { SIZE_OPTIONS } from '../constants/size';

const getThemeClassName = (val) => `theme--${val}`;
const getSchemeClassName = (val) => `scheme--${val}`;

export const ViewEvent = {
    PROGRESS_CHANGED: 'progress-changed'
};

class SpaceView extends EventDispatcher {
    _isStarted = false;

    _hasBeenInvalidatedFirst = false;

    _isResized = false;

    _isProgressChanged = false;

    _isResizedOnce = false;

    _autoUpdate = true;

    constructor() {
        super();

        this._construct();
    }

    _construct() {
        //
        // 1. Add Common Camera Rig

        //
        // 2. Add the 3D infrastructure and gradient background
        this.threeViewShell = new ThreeShell();

        this._components = [];
    }

    get domElement() {
        const { domElement } = this.threeViewShell;

        return domElement || null;
    }

    appendCanvasToElement(el) {
        const { domElement } = this.threeViewShell;

        if (!domElement) return null;

        if (typeof el === 'string') el = document.querySelector(el);

        el.appendChild(domElement);

        return domElement;
    }

    update() {
        if (!this._isStarted) return;
        if (this._isResized) {
            this._resize(!this._isResizedOnce);
            this._isResized = false;
            this._isResizedOnce = true;
        }

        if (this._ticker && this._ticker.isPlaying) this._invalidate(this._ticker.time, this._ticker.delta);

        this._render();
    }

    start() {
        this._isStarted = true;

        this.addResizeHandler();

        this.update();

        if (this.hasRenderer) {
            this._ticker = this._ticker || new Ticker();
            this._ticker.addEventListener(Ticker.EVENT_TICK, this._tickHandler, this);
            this._ticker.start();

            this.threeViewShell.start();
            this._startComponents();
        }
    }

    pause() {
        this._ticker.pause();
    }

    addComponents(newCompsArray) {
        this._components = [...this._components, ...newCompsArray];
    }

    _startComponents() {
        if (!this.hasRenderer) return;

        this._components.forEach((comp) => {
            comp.updateAnimationProgress();

            comp.start();
        });
    }

    _tickHandler() {
        if (this._autoUpdate) this.update();
    }

    addResizeHandler() {
        this._handleResize();

        this._resizeHandler = () => {
            this._handleResize();
        };
        window.addEventListener('resize', this._resizeHandler);
        window.addEventListener('orientationchange', this._resizeHandler);
    }

    _handleResize() {
        this._isResized = true;
    }

    _render() {
        if (!this.hasRenderer) return;

        this.threeViewShell.render();
    }

    _invalidate(t, dt) {
        if (!this.hasRenderer) return;

        this.threeViewShell.invalidate(t, dt);

        const { schemeChanged } = this.threeViewShell;
        const { camera } = this.threeViewShell.infrastructure;

        if (this._isProgressChanged) camera.updateWorldMatrix(false, false);

        this._components.forEach((comp) => {
            if (!comp.enabled) return;

            if (comp.hasInvalidate) comp.invalidate(t, dt);
            if (schemeChanged && comp.canReciveScheme) comp.globalScheme = this.threeViewShell.scheme;
            if (!this._hasBeenInvalidatedFirst || this._isProgressChanged) {
                comp.updateAnimationProgress();
                this._checkSchemeAndTheme(comp, camera);
            }
        });

        if (!this._hasBeenInvalidatedFirst || this._isProgressChanged) {
            this.dispatchEvent({
                type: ViewEvent.PROGRESS_CHANGED,
                progress: this.progress
            });
        }

        this._hasBeenInvalidatedFirst = true;
        this._isProgressChanged = false;
        this.threeViewShell.schemeChanged = false;
    }

    _resize(force) {
        if (!this.hasRenderer) return;

        const w = window.innerWidth;
        const h = window.innerHeight;
        const orientation = w > h ? SIZE_OPTIONS.LANDSCAPE : SIZE_OPTIONS.PORTRAIT;
        const pixelRatio = this.threeViewShell.infrastructure.renderer.getPixelRatio();

        this.threeViewShell.resize(force);

        this._components.forEach((comp) => {
            if (comp.hasResize) comp.resize(w, h, orientation, force, pixelRatio);
        });
    }

    set progress(value) {
        this.threeViewShell.progress = value;
        this._isProgressChanged = true;
    }

    get progress() {
        return this.threeViewShell.progress;
    }

    set autoUpdate(value) {
        this._autoUpdate = value;
    }

    get autoUpdate() {
        return this._autoUpdate;
    }

    get hasRenderer() {
        return this.threeViewShell.hasRenderer;
    }

    _checkSchemeAndTheme(comp, camera) {
        if (!comp.canChangeScheme) return;

        const {
            changed, newScheme, newTheme, prevTheme, prevScheme
        } = comp.checkSchemeChanged(camera);

        if (!changed) return;

        if (this._themeTimeout) clearTimeout(this._themeTimeout);

        this.threeViewShell.scheme = newScheme;
        document.body.classList.remove(getThemeClassName(prevTheme));
        document.body.classList.add(getThemeClassName(newTheme));
        document.body.classList.remove(getSchemeClassName(prevScheme));
        document.body.classList.add(getSchemeClassName(newScheme));
        document.body.classList.add('theme-changing');

        this._themeTimeout = setTimeout(() => {
            document.body.classList.remove('theme-changing');

            clearTimeout(this._themeTimeout);
            this._themeTimeout = null;
        }, 1000);
    }
}

export default SpaceView;
